// The live quality controller (#32, ADR 0003): picks the tier the voice plays
// from the load monitor's samples (load-monitor.ts), stepping down a live tier
// before the audio drops out and back up when there is room again.
//
// The voice switches tiers with its `quality` control (the tier's index in
// data/quality/<module>.toml). The module builds every tier when the voice is
// built and crossfades between two of them for 20 ms, so a switch allocates
// nothing on the audio thread (docs/contracts/module-interface.md § Tiers).
// The controller itself only decides; the quality panel
// (web/src/app/panels/quality/) sends its decisions to the running voice.
//
// The rules, with the thresholds of QualityPolicy:
//
// - **Down** one live tier as soon as the load over the last second reaches
//   `downLoad`, the load over the last quarter second reaches
//   `suddenLoad` (a sudden overload, caught in a quarter of the time), or on
//   a deadline miss while the load is at least `missLoad`. Below that the
//   miss has another cause, such as a stalled virtual machine (#6), and a
//   cheaper tier would not help.
// - **Up** one live tier when the higher tier's estimated load has stayed below
//   `upLoad` for `upHoldS`, with no miss. The estimate is the current load
//   times the two tiers' cost ratio, measured across the last switch between
//   them (the load just before it over the load just after), or `defaultRatio`
//   until there was one. Each time the controller has to leave
//   a tier again, its hold doubles (up to `maxUpHoldS`), so a computer at the
//   edge settles instead of switching back and forth.
// - After a switch nothing is judged until a whole window lies after it.
// - In auto, only live tiers play: the full tier is for offline renders and
//   the loop-set tier (P5.5) is not built yet. A chosen tier plays as chosen.

import type { LoadSample } from "./load-monitor";

export type TierKind = "full" | "live" | "loop-set";

/** One tier of a module's quality table. */
export interface QualityTier {
  id: string;
  kind: TierKind;
  description: string;
}

/** A module's tiers in index order (src/generated/quality/<module>.json, from data/quality/). */
export interface QualityTable {
  module: string;
  tiers: QualityTier[];
}

/** The view state's quality value that lets the controller choose (docs/contracts/view-state.md). */
export const AUTO = "auto";

let tables: Record<string, QualityTable> | null = null;

/** The quality table of a module, or null for a module without one. */
export function qualityTable(module: string): QualityTable | null {
  // Looked up on first use, so the controller also loads where Vite does not run (the unit tests).
  tables ??= import.meta.glob<QualityTable>("../../generated/quality/*.json", { eager: true, import: "default" });
  return tables[`../../generated/quality/${module}.json`] ?? null;
}

export interface QualityPolicy {
  /** Step down when the load reaches this share of the audio thread. */
  downLoad: number;
  /** Step down when the load over the short window reaches this. */
  suddenLoad: number;
  /** Step down on a deadline miss while the load is at least this. */
  missLoad: number;
  /** Step up when the higher tier's estimated load stays below this... */
  upLoad: number;
  /** ...for this long, s of audio... */
  upHoldS: number;
  /** ...which doubles each time the tier had to be left again, up to this. */
  maxUpHoldS: number;
  /** Cost of a live tier over the next cheaper one until a switch between them measures it. */
  defaultRatio: number;
  /** How long after a switch, beyond the window, nothing is judged (the crossfade and the message), s. */
  settleS: number;
}

/**
 * The thresholds. The audio thread of Chromium in the cloud VM takes up to 71 %
 * busy work with no underrun and underruns from about 95 % (#6, REPORT.md), so
 * the controller steps down well below the knee.
 */
export const DEFAULT_POLICY: QualityPolicy = {
  downLoad: 0.7,
  suddenLoad: 0.9,
  missLoad: 0.35,
  upLoad: 0.5,
  upHoldS: 10,
  maxUpHoldS: 320,
  defaultRatio: 2,
  settleS: 0.1,
};

export type SwitchReason = "start" | "chosen" | "overload" | "miss" | "headroom";

/** One change of tier. */
export interface QualitySwitch {
  /** Audio time of the sample that caused it (0 for a start or a choice before any sample), s. */
  time: number;
  from: number;
  to: number;
  reason: SwitchReason;
  /** The load that caused it, or null for a choice. */
  load: number | null;
}

export class QualityController {
  readonly table: QualityTable;
  readonly policy: QualityPolicy;
  /** Every change of tier, oldest first. */
  readonly history: QualitySwitch[] = [];
  /** The newest sample. */
  lastSample: LoadSample | null = null;

  private choiceValue = AUTO;
  private current: number;
  /** Indices of the live tiers, most detailed first. */
  private readonly live: number[];
  /** Cost of each live tier over the next cheaper one, measured across a switch, by the higher tier's index. */
  private readonly ratios = new Map<number, number>();
  /** The tier left at the last switch, and its load then, until the first settled sample after it. */
  private left: { tier: number; load: number } | null = null;
  /** How often each tier had to be left for overload. */
  private readonly exits = new Map<number, number>();
  private calmSince: number | null = null;
  private settleUntil = Number.NEGATIVE_INFINITY;
  private lastTime = 0;

  constructor(table: QualityTable, choice: string = AUTO, policy: Partial<QualityPolicy> = {}) {
    this.table = table;
    this.policy = { ...DEFAULT_POLICY, ...policy };
    this.live = table.tiers.flatMap((t, i) => (t.kind === "live" ? [i] : []));
    this.current = this.live[0] ?? 0;
    this.choose(choice);
    this.history.length = 0;
    this.history.push({ time: 0, from: this.current, to: this.current, reason: "start", load: null });
  }

  /** The tier playing (its index in the table). */
  get tier(): number {
    return this.current;
  }

  get tierId(): string {
    return this.table.tiers[this.current]?.id ?? "";
  }

  /** "auto", or the id of the chosen tier. */
  get choice(): string {
    return this.choiceValue;
  }

  /** Whether the controller chooses the tier. */
  get auto(): boolean {
    return this.choiceValue === AUTO;
  }

  /** The tier a voice that starts in auto plays: the first live tier, or the first tier. */
  get defaultTier(): number {
    return this.live[0] ?? 0;
  }

  /** The indices of the live tiers, most detailed first. */
  get liveTiers(): readonly number[] {
    return this.live;
  }

  /** How long the higher tier must look affordable before the controller steps up to it, s. */
  holdFor(tier: number): number {
    const exits = this.exits.get(tier) ?? 0;
    return Math.min(this.policy.maxUpHoldS, this.policy.upHoldS * 2 ** Math.max(0, exits - 1));
  }

  /**
   * "auto" or a tier id (an id the table does not have counts as auto). Auto
   * starts again from the default tier and steps down from there. Returns the
   * new tier when the choice changes the tier, else null.
   */
  choose(choice: string): number | null {
    const index = this.table.tiers.findIndex((t) => t.id === choice);
    this.choiceValue = index < 0 ? AUTO : choice;
    const to = index < 0 ? this.defaultTier : index;
    this.calmSince = null;
    return to === this.current ? null : this.switchTo(to, "chosen", this.lastTime, null);
  }

  /** Takes a sample. Returns the tier to switch to, or null to stay. */
  update(sample: LoadSample): number | null {
    this.lastSample = sample;
    this.lastTime = sample.end;
    if (!this.auto || sample.recentStart < this.settleUntil) return null;
    const pos = this.live.indexOf(this.current);
    if (pos < 0) return null;
    const p = this.policy;
    // Until a whole window lies after the last switch, only a sudden overload counts.
    const settled = sample.start >= this.settleUntil;
    if (settled && this.left !== null) this.learn(this.left, sample.load);

    let reason: SwitchReason | null = null;
    if (sample.recentLoad >= p.suddenLoad || (settled && sample.load >= p.downLoad)) reason = "overload";
    else if (settled && sample.misses > 0 && sample.load >= p.missLoad) reason = "miss";
    if (reason !== null) {
      this.calmSince = null;
      const lower = this.live[pos + 1];
      if (lower === undefined) return null; // the cheapest live tier: nothing left to step down to
      this.exits.set(this.current, (this.exits.get(this.current) ?? 0) + 1);
      return this.switchTo(lower, reason, sample.end, Math.max(sample.load, sample.recentLoad));
    }

    const higher = this.live[pos - 1];
    if (higher === undefined || !settled) return null;
    const ratio = this.ratios.get(higher) ?? p.defaultRatio;
    if (sample.misses > 0 || sample.load * ratio >= p.upLoad) {
      this.calmSince = null;
      return null;
    }
    this.calmSince ??= sample.end;
    if (sample.end - this.calmSince < this.holdFor(higher)) return null;
    return this.switchTo(higher, "headroom", sample.end, sample.load);
  }

  /** The cost ratio of two neighbouring live tiers from the loads on either side of a switch. */
  private learn(left: { tier: number; load: number }, load: number): void {
    this.left = null;
    const [a, b] = [this.live.indexOf(left.tier), this.live.indexOf(this.current)];
    if (a < 0 || b < 0 || Math.abs(a - b) !== 1 || load <= 0 || left.load <= 0) return;
    const [higher, ratio] = a < b ? [left.tier, left.load / load] : [this.current, load / left.load];
    this.ratios.set(higher, Math.max(1, ratio));
  }

  private switchTo(to: number, reason: SwitchReason, time: number, load: number | null): number {
    this.history.push({ time, from: this.current, to, reason, load });
    this.left = load === null ? null : { tier: this.current, load };
    this.current = to;
    this.calmSince = null;
    this.settleUntil = time + this.policy.settleS;
    return to;
  }
}
